/**
 * The gateway's text-layout primitives, ported for the paper preview.
 *
 * PORTS, statement for statement, of apps/gateway/internal/renderer/
 * text_layout.go (sanitize, displayWidth, wrapHanging, padStart, encodeCP850)
 * and escpos_format.go (padLinePrefixed, writeInLine, indentOf) plus
 * document_v2.go (fillLine). The preview must lay a line out exactly where the
 * gateway does, and these are the functions that decide where that is.
 *
 * They are not a second SOURCE of layout: nothing prints through them. They are
 * held to the gateway by the preview-grid parity corpus
 * (packages/print-conformance/fixtures/preview-grid/), whose every row was
 * produced by the Go functions these mirror — a divergence is a red test, not
 * a photograph.
 *
 * CHARACTER SET (docs/superpowers/specs/2026-10-24-printer-character-set-and-
 * typographic-receipts-design.md §1). Every primitive takes the PRINTER's
 * `characterSet`, default `Western` — today's PC850 path, byte for byte. A
 * `Chinese` printer keeps ASCII plus the GBK double-byte keep set, and every
 * width is a COLUMN count: ASCII 1, a kept double-byte character 2. The `*For`
 * functions are the charset-aware ones; the bare names are their Western
 * forms, unchanged for every existing caller. Held with Go and Dart by
 * packages/print-conformance/fixtures/charset.json.
 */

/** The printer's character set — `IPrinter.characterSet`. */
export type PaperCharacterSet = 'Western' | 'Chinese';

/**
 * CP850 bytes 0x80..0xFF, as `golang.org/x/text/encoding/charmap.CodePage850`
 * decodes them (generated from that table, not typed from a chart).
 */
const CP850_HIGH =
  '\u00c7\u00fc\u00e9\u00e2\u00e4\u00e0\u00e5\u00e7\u00ea\u00eb\u00e8\u00ef\u00ee\u00ec\u00c4\u00c5' +
  '\u00c9\u00e6\u00c6\u00f4\u00f6\u00f2\u00fb\u00f9\u00ff\u00d6\u00dc\u00f8\u00a3\u00d8\u00d7\u0192' +
  '\u00e1\u00ed\u00f3\u00fa\u00f1\u00d1\u00aa\u00ba\u00bf\u00ae\u00ac\u00bd\u00bc\u00a1\u00ab\u00bb' +
  '\u2591\u2592\u2593\u2502\u2524\u00c1\u00c2\u00c0\u00a9\u2563\u2551\u2557\u255d\u00a2\u00a5\u2510' +
  '\u2514\u2534\u252c\u251c\u2500\u253c\u00e3\u00c3\u255a\u2554\u2569\u2566\u2560\u2550\u256c\u00a4' +
  '\u00f0\u00d0\u00ca\u00cb\u00c8\u0131\u00cd\u00ce\u00cf\u2518\u250c\u2588\u2584\u00a6\u00cc\u2580' +
  '\u00d3\u00df\u00d4\u00d2\u00f5\u00d5\u00b5\u00fe\u00de\u00da\u00db\u00d9\u00fd\u00dd\u00af\u00b4' +
  '\u00ad\u00b1\u2017\u00be\u00b6\u00a7\u00f7\u00b8\u00b0\u00a8\u00b7\u00b9\u00b3\u00b2\u25a0\u00a0';

const CP850_ENCODABLE: ReadonlySet<string> = new Set(Array.from(CP850_HIGH));

/** Can the PC850 code page print this one character (one code point)? */
export function isCp850Encodable(ch: string): boolean {
  const cp = ch.codePointAt(0) ?? 0;
  return cp < 0x80 || CP850_ENCODABLE.has(ch);
}

/** text_layout.go `typographic` — screen typography onto ASCII, one rune at a time. */
const TYPOGRAPHIC: ReadonlyMap<string, string> = new Map([
  ['×', 'x'], ['✕', 'x'], ['✖', 'x'], ['⨯', 'x'],
  ['—', '-'], ['–', '-'], ['‒', '-'], ['―', '-'], ['−', '-'],
  ['“', '"'], ['”', '"'], ['„', '"'], ['‟', '"'],
  ['‘', "'"], ['’', "'"], ['‚', "'"], ['‛', "'"], ['′', "'"],
  ['…', '...'],
  ['•', '*'], ['▪', '*'], ['◦', '*'], ['‣', '*'],
  ['→', '->'], ['←', '<-'], ['⇒', '=>'],
  ['½', '1/2'], ['¼', '1/4'], ['¾', '3/4'], ['⅓', '1/3'], ['⅔', '2/3'],
  ['€', 'EUR'], ['™', '(TM)'], ['℠', '(SM)'], ['℃', 'C'], ['℉', 'F'],
  ['\u00a0', ' '], ['\u2007', ' '], ['\u2009', ' '], ['\u202f', ' '], ['\u2002', ' '],
  ['\u200b', ''], ['\u200c', ''], ['\u200d', ''], ['\ufeff', ''],
  ['\t', ' '], ['\r', ''],
]);

/** text_layout.go `latinFallback`. */
const LATIN_FALLBACK: ReadonlyMap<string, string> = new Map([
  ['ø', 'o'], ['Ø', 'O'], ['đ', 'd'], ['Đ', 'D'], ['ħ', 'h'], ['Ħ', 'H'],
  ['ł', 'l'], ['Ł', 'L'], ['ß', 'ss'], ['æ', 'ae'], ['Æ', 'AE'],
  ['œ', 'oe'], ['Œ', 'OE'], ['þ', 'th'], ['Þ', 'TH'], ['ð', 'd'], ['Ð', 'D'],
  ['ı', 'i'], ['İ', 'I'], ['ŋ', 'n'], ['Ŋ', 'N'],
]);

const COMBINING_MARK = /\p{Mn}/u;

function isPrintableAscii(s: string): boolean {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c === 0x0a) continue;
    if (c < 0x20 || c >= 0x7f) return false;
  }
  return true;
}

function transliterate(s: string): string {
  if (isPrintableAscii(s)) return s;
  let out = '';
  for (const ch of s) out += TYPOGRAPHIC.get(ch) ?? ch;
  return out;
}

function foldToAscii(ch: string): string {
  let out = '';
  for (const c of ch.normalize('NFD')) {
    if (COMBINING_MARK.test(c)) continue;
    if ((c.codePointAt(0) ?? 0) < 0x80) {
      out += c;
      continue;
    }
    const fallback = LATIN_FALLBACK.get(c);
    if (fallback === undefined) return '';
    out += fallback;
  }
  return out;
}

/**
 * The Chinese keep set (design §1): CJK Unified Ideographs U+4E00–U+9FA5, CJK
 * punctuation U+3000–U+3003 and U+3008–U+3011, fullwidth forms U+FF01–U+FF5E.
 * Every one is a GBK double-byte code. ASCII is kept too, single-byte.
 */
export function isChineseDoubleByte(ch: string): boolean {
  const cp = ch.codePointAt(0) ?? 0;
  return (
    (cp >= 0x4e00 && cp <= 0x9fa5) ||
    (cp >= 0x3000 && cp <= 0x3003) ||
    (cp >= 0x3008 && cp <= 0x3011) ||
    (cp >= 0xff01 && cp <= 0xff5e)
  );
}

/**
 * The columns one character occupies on a printer with this character set:
 * 2 for a kept double-byte character on a Chinese printer, otherwise 1.
 */
export function charColumns(ch: string, charset: PaperCharacterSet = 'Western'): number {
  return charset === 'Chinese' && isChineseDoubleByte(ch) ? 2 : 1;
}

/** The column count of one line (no "\n") of already-sanitized text. */
export function lineColumns(s: string, charset: PaperCharacterSet = 'Western'): number {
  let n = 0;
  for (const ch of s) n += charColumns(ch, charset);
  return n;
}

/**
 * `sanitize` for a printer's character set. Western is `sanitize`. Chinese
 * keeps ASCII and the keep set; everything else folds exactly as Western
 * folds it (typography, accents → ASCII) or is dropped; a control character
 * becomes a space.
 */
export function sanitizeFor(s: string, charset: PaperCharacterSet = 'Western'): string {
  if (charset !== 'Chinese') return sanitize(s);
  if (isPrintableAscii(s)) return s;
  const t = transliterate(s);
  let out = '';
  for (const ch of t) {
    const cp = ch.codePointAt(0) ?? 0;
    if (ch === '\n') out += ch;
    else if (cp < 0x20 || cp === 0x7f) out += ' ';
    else if (cp < 0x80) out += ch;
    else if (isChineseDoubleByte(ch)) out += ch;
    else out += foldToAscii(ch);
  }
  return out;
}

/** text_layout.go `sanitize`: one rune in, one column out, no command bytes. */
export function sanitize(s: string): string {
  if (isPrintableAscii(s)) return s;
  const t = transliterate(s);
  let out = '';
  for (const ch of t) {
    const cp = ch.codePointAt(0) ?? 0;
    if (ch === '\n') out += ch;
    else if (cp < 0x20 || cp === 0x7f) out += ' ';
    else if (cp < 0x80) out += ch;
    else if (CP850_ENCODABLE.has(ch)) out += ch;
    else out += foldToAscii(ch);
  }
  return out;
}

/** Rune count. */
function runeLength(s: string): number {
  let n = 0;
  for (const _ of s) n += 1;
  return n;
}

/** text_layout.go `displayWidth`: the widest line's column count. */
export function displayWidth(s: string): number {
  let widest = 0;
  for (const line of sanitize(s).split('\n')) widest = Math.max(widest, runeLength(line));
  return widest;
}

/** `displayWidth` for a printer's character set: the widest line, in COLUMNS. */
export function displayWidthFor(s: string, charset: PaperCharacterSet = 'Western'): number {
  if (charset !== 'Chinese') return displayWidth(s);
  let widest = 0;
  for (const line of sanitizeFor(s, charset).split('\n')) widest = Math.max(widest, lineColumns(line, charset));
  return widest;
}

/**
 * The longest prefix of `s` (sanitized) that fits `width` columns. A
 * double-width character that would overrun is left out, never split.
 */
function takeColumns(chars: readonly string[], width: number, charset: PaperCharacterSet): string[] {
  const out: string[] = [];
  let used = 0;
  for (const ch of chars) {
    const w = charColumns(ch, charset);
    if (used + w > width) break;
    out.push(ch);
    used += w;
  }
  return out;
}

/**
 * text_layout.go `truncate`, by COLUMNS: clip `s` to `width` columns for a
 * field that must not wrap, marking the clip with "..." (3 columns). A
 * double-width character is never split — the column it cannot fill is left
 * empty.
 */
export function truncateFor(s: string, width: number, charset: PaperCharacterSet = 'Western'): string {
  const t = sanitizeFor(s, charset);
  if (width < 1) return '';
  if (lineColumns(t, charset) <= width) return t;
  const chars = Array.from(t);
  if (width <= 3) return takeColumns(chars, width, charset).join('');
  return takeColumns(chars, width - 3, charset).join('') + '...';
}

/** text_layout.go `truncate` (Western). */
export function truncate(s: string, width: number): string {
  return truncateFor(s, width, 'Western');
}

/**
 * The characters encodeCP850 puts on the wire for `s`, read back — what a
 * VERBATIM line prints: an unencodable character becomes "?". Control
 * characters print nothing.
 */
export function printedVerbatim(s: string): string {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp < 0x20 || cp === 0x7f) continue;
    out += isCp850Encodable(ch) ? ch : '?';
  }
  return out;
}

/**
 * `printedVerbatim` for a printer's character set. On a Chinese printer the
 * wire is GBK, so a kept double-byte character prints itself and any other
 * non-ASCII character is "?".
 */
export function printedVerbatimFor(s: string, charset: PaperCharacterSet = 'Western'): string {
  if (charset !== 'Chinese') return printedVerbatim(s);
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp < 0x20 || cp === 0x7f) continue;
    out += cp < 0x80 || isChineseDoubleByte(ch) ? ch : '?';
  }
  return out;
}

/**
 * Go `strings.Fields` over sanitized text. U+3000 (the ideographic space) is a
 * Go `unicode.IsSpace` rune too, and only a Chinese printer's sanitize can
 * leave one in the text — so Western is unchanged.
 */
function fields(s: string): string[] {
  return s.split(/[ \t\n\v\f\r\u0085\u00a0\u3000]+/).filter((f) => f.length > 0);
}

/** text_layout.go `wrapHanging`. Continuations are returned WITHOUT the indent. */
export function wrapHanging(s: string, first: number, rest: number): string[] {
  return wrapHangingFor(s, first, rest, 'Western');
}

/**
 * `wrapHanging` by COLUMNS for a printer's character set. A word too wide for
 * a line is hard-split at the last character that fits; a double-width
 * character is never split (at least one character is always taken, so a
 * one-column line still makes progress).
 */
export function wrapHangingFor(s: string, first: number, rest: number, charset: PaperCharacterSet = 'Western'): string[] {
  const f = Math.max(first, 1);
  const r = Math.max(rest, 1);
  const out: string[] = [];
  let cur: string[] = [];
  let curCols = 0;
  const limit = () => (out.length === 0 ? f : r);
  const flush = () => {
    out.push(cur.join(''));
    cur = [];
    curCols = 0;
  };
  for (const word of fields(s)) {
    let wr = Array.from(word);
    for (;;) {
      const wc = lineColumns(wr.join(''), charset);
      if (cur.length === 0 && wc <= limit()) {
        cur.push(...wr);
        curCols += wc;
      } else if (cur.length === 0) {
        const head = takeColumns(wr, limit(), charset);
        const n = Math.max(head.length, 1);
        cur.push(...wr.slice(0, n));
        wr = wr.slice(n);
        flush();
        continue;
      } else if (curCols + 1 + wc <= limit()) {
        cur.push(' ', ...wr);
        curCols += 1 + wc;
      } else {
        flush();
        continue;
      }
      break;
    }
  }
  if (cur.length > 0) flush();
  return out.length === 0 ? [''] : out;
}

/** text_layout.go `padStart`. */
export function padStart(s: string, width: number, charset: PaperCharacterSet = 'Western'): string {
  const gap = width - displayWidthFor(s, charset);
  return gap < 1 ? s : ' '.repeat(gap) + s;
}

/** escpos_format.go `continuationIndent`. */
const CONTINUATION_INDENT = '  ';

/** escpos_format.go `padLinePrefixed`. The result may contain "\n". */
export function padLinePrefixed(prefixIn: string, labelIn: string, rightIn: string, width: number): string {
  return padLinePrefixedFor(prefixIn, labelIn, rightIn, width, 'Western');
}

/** `padLinePrefixed` by COLUMNS for a printer's character set. */
export function padLinePrefixedFor(
  prefixIn: string,
  labelIn: string,
  rightIn: string,
  width: number,
  charset: PaperCharacterSet = 'Western',
): string {
  const dw = (x: string) => displayWidthFor(x, charset);
  let prefix = sanitizeFor(prefixIn, charset);
  let label = sanitizeFor(labelIn, charset);
  const right = sanitizeFor(rightIn, charset);

  const trimmed = label.replace(/^ +/, '');
  if (trimmed !== label) {
    prefix += label.slice(0, label.length - trimmed.length);
    label = trimmed;
  }

  let indent = prefix === '' ? CONTINUATION_INDENT : prefix;
  indent = ' '.repeat(dw(indent));
  const pw = dw(prefix);

  if (width <= 0) {
    if (right === '') return prefix + label;
    return `${prefix}${label} ${right}`;
  }

  const rw = dw(right);
  let avail = width - pw;
  if (right !== '') avail -= rw + 1;
  if (avail < 1) {
    if (right === '') return prefix + label;
    return `${prefix}${label}\n${padStart(right, width, charset)}`;
  }

  const lines = wrapHangingFor(label, avail, width - dw(indent) - (right !== '' ? rw + 1 : 0), charset);
  let b = '';
  lines.forEach((line, i) => {
    if (i === 0) b += prefix;
    else b += `\n${indent}`;
    b += line;
    if (right === '' || i < lines.length - 1) return;
    const lead = i > 0 ? dw(indent) : pw;
    const gap = Math.max(width - lead - dw(line) - rw, 1);
    b += ' '.repeat(gap) + right;
  });
  return b;
}

/** escpos_format.go `padLine`: a label against a right-aligned value, by COLUMNS. */
export function padLineFor(left: string, right: string, width: number, charset: PaperCharacterSet = 'Western'): string {
  return padLinePrefixedFor('', left, right, width, charset);
}

/** escpos_format.go `padLine` (Western). */
export function padLine(left: string, right: string, width: number): string {
  return padLineFor(left, right, width, 'Western');
}

/** escpos_format.go `writeInLine`: `label  $____` to the width, at least three blanks. */
export function writeInLine(label: string, width: number, charset: PaperCharacterSet = 'Western'): string {
  const prefix = `${sanitizeFor(label, charset)}  $`;
  const blanks = Math.max(width - displayWidthFor(prefix, charset), 3);
  return prefix + '_'.repeat(blanks);
}

/** document_v2.go `fillLine`: `prefix` then `char` to the end of the line. */
export function fillLine(prefix: string, char: string, cols: number, charset: PaperCharacterSet = 'Western'): string {
  const n = Math.max(cols - displayWidthFor(prefix, charset), 0);
  return prefix + char.repeat(n);
}

/** escpos.go `columnsForPaperWidth`. */
export function columnsForPaperWidth(mm: number): number {
  if (mm === 58) return 32;
  if (mm === 76) return 40;
  return 48;
}
